import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
  Bot,
  ChevronRight,
  MessageSquare,
  MoreHorizontal,
  Plus,
  Power,
  PowerOff,
  Search,
  Loader2,
  Sparkles,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import Versions from "../versions";
import ChatBotFlowEditWrapper from "../chatbot-flow-edit/wrapper";
import AdminLayout from "../../components/admin-layout";
import SelectFunction from "../select-function";
import { toast } from "sonner";
import UserContext from "../../context/users";
import { AdminRole, Weekdays } from "../../constants";
import { createChatbot, deleteChatbot, getChatBots, getChatbotStats, updateChatBot } from "../../services/chatbot";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "../../components/ui/dialog";
import { DropdownMenu } from "../../components/ui/dropdown-menu";
import { EmptyState } from "../../components/ui/empty-state";
import { Pagination } from "../../components/ui/pagination";
import { cn } from "../../lib/cn";
import useDebounce from "../../hooks/useDebounce";

export default function ChatBots() {
  return (
    <Routes>
      <Route path={"/:id/versions/editor/:snapshot/*"} element={<SelectFunction />} />

      <Route path={"*"} element={<Routes>
        <Route index path={"/"} element={
          <AdminLayout noPadding withBackground={false}>
            <ChatBotIndex />
          </AdminLayout>
        } />
        <Route path={"/:id"} element={
          <AdminLayout>
            <ChatBotFlowEditWrapper />
          </AdminLayout>
        } />
        <Route path={"/:id/versions/*"} element={
          <AdminLayout>
            <Versions />
          </AdminLayout>
        } />
      </Routes>} />
    </Routes>
  );
}

function ChatBotIndex() {
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [user] = useContext(UserContext);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const navigate = useNavigate();
  const isAdmin = user?.role === AdminRole;
  const [stats, setStats] = useState({ total: 0, active: 0, messages: 0, sessions: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    setStatsLoading(true);
    getChatbotStats()
      .then(({ data }) => setStats(data))
      .catch(console.error)
      .finally(() => setStatsLoading(false));
  }, []);

  const reloadList = useCallback(() => {
    setLoading(true);
    getChatBots({ page, perPage, graph: true, search: debouncedSearch, searchField: "name" })
      .then(({ data: list, total: t }) => {
        setData(list || []);
        setTotal(t || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, perPage, debouncedSearch]);

  useEffect(() => { reloadList(); }, [reloadList]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const handleCreate = useCallback(() => {
    if (!newName.trim()) return;
    setCreating(true);
    createChatbot({
      name: newName,
      description: newDescription,
      isActive: true,
      infra: "EMBED",
      type: "FLOW",
      aiModel: "text-embedding-3-small",
    })
      .then(({ data: created }) => {
        const dest = created?.active?.length > 0
          ? `${created.id}/versions/editor/${created.active}`
          : `${created.id}/versions`;
        navigate(dest);
        toast.success("Chatbot criado!");
      })
      .catch(({ data }) => {
        toast.error(
          data?.errorMessage === "max chatbots reached"
            ? "Limite de chatbots atingido."
            : "Falha ao criar chatbot"
        );
      })
      .finally(() => {
        setCreating(false);
        setCreateOpen(false);
        setNewName("");
        setNewDescription("");
      });
  }, [newName, newDescription, navigate]);

  return (
    <div className="dash-scroll flex h-full w-full flex-col overflow-y-auto bg-[var(--dash-bg)]">
      <div className="mx-auto w-full max-w-[1400px] px-6 py-8">
        <header className="mb-6 flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold tracking-tight text-[var(--dash-text)]">Fluxo</h1>
              {statsLoading ? (
                <div className="mt-1 flex items-center gap-2">
                  <div className="animate-pulse rounded-md bg-[var(--dash-surface-hover)] h-3.5 w-24" />
                  <span className="text-xs text-[var(--dash-text-faint)]">·</span>
                  <div className="animate-pulse rounded-md bg-[var(--dash-surface-hover)] h-3.5 w-16" />
                </div>
              ) : (
                <p className="text-xs text-[var(--dash-text-muted)]">
                  {stats.total} chatbot{stats.total !== 1 ? "s" : ""} · {stats.active} ativo{stats.active !== 1 ? "s" : ""}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="w-60">
                <Input
                  leftIcon={Search}
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  rightSlot={<SearchFeedback loading={loading} hasValue={!!search} onClear={() => setSearch("")} />}
                />
              </div>
              <Button variant="primary" leftIcon={Plus} onClick={() => setCreateOpen(true)}>
                Novo
              </Button>
            </div>
          </div>

          {!statsLoading && (
            <div className="flex flex-wrap gap-3">
              <StatPill icon={Bot} label="Total" value={stats.total} />
              <StatPill icon={Zap} label="Ativos" value={stats.active} variant="success" />
              <StatPill icon={MessageSquare} label="Msgs/mês" value={stats.messages.toLocaleString()} />
            </div>
          )}
        </header>

        {loading && data.length === 0 ? (
          <ListSkeleton isAdmin={isAdmin} />
        ) : data.length === 0 ? (
          <div className="rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
            <EmptyState
              icon={Sparkles}
              title={debouncedSearch ? "Nenhum resultado" : "Crie seu primeiro chatbot"}
              description={
                debouncedSearch
                  ? `Nada encontrado para "${debouncedSearch}".`
                  : "Chatbots de fluxo para automatizar conversas com seus clientes."
              }
              action={
                !debouncedSearch && (
                  <Button variant="primary" size="lg" leftIcon={Plus} onClick={() => setCreateOpen(true)}>
                    Criar primeiro chatbot
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
            <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-muted)] md:flex">
              <div className="w-6" />
              <div className="flex-1">Nome</div>
              {isAdmin && <div className="w-28">Empresa</div>}
              <div className="w-20 text-right">Msgs/mês</div>
              <div className="w-[72px]">Tendência</div>
              <div className="w-8" />
            </div>

            <div className="divide-y divide-[var(--dash-border)]">
              {data.map((item, i) => (
                <ChatbotRow key={item.id} data={item} index={i} isAdmin={isAdmin} reloadList={reloadList} />
              ))}
            </div>
          </div>
        )}

        {total > perPage && (
          <Pagination page={page} pageSize={perPage} total={total} onPageChange={setPage} />
        )}
      </div>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} size="sm">
        <DialogHeader icon={Bot} title="Novo chatbot" description="Crie o chatbot e configure o fluxo." />
        <DialogContent>
          <form
            onSubmit={(e) => { e.preventDefault(); handleCreate(); }}
            className="flex flex-col gap-3"
          >
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nome do chatbot"
              className="h-11 w-full rounded-xl bg-[var(--dash-surface-subtle)] px-4 text-sm text-[var(--dash-text)] outline-none ring-1 ring-[var(--dash-border)] transition-all placeholder:text-[var(--dash-text-faint)] focus:ring-2 focus:ring-[var(--dash-primary)]/50"
            />
            <input
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Descrição (opcional)"
              className="h-11 w-full rounded-xl bg-[var(--dash-surface-subtle)] px-4 text-sm text-[var(--dash-text)] outline-none ring-1 ring-[var(--dash-border)] transition-all placeholder:text-[var(--dash-text-faint)] focus:ring-2 focus:ring-[var(--dash-primary)]/50"
            />
          </form>
        </DialogContent>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleCreate} loading={creating} disabled={!newName.trim()}>
            Criar
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

function StatPill({ icon: Icon, label, value, variant }) {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-[var(--dash-surface)] px-3 py-2 ring-1 ring-[var(--dash-border)]">
      <div
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-md",
          variant === "success"
            ? "bg-[rgba(52,254,173,0.1)] text-[#1fb37c]"
            : "bg-[var(--dash-primary-soft)] text-[var(--dash-primary)]"
        )}
      >
        <Icon size={12} strokeWidth={2.4} />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-sm font-semibold tabular-nums text-[var(--dash-text)]">{value}</span>
        <span className="text-[10px] text-[var(--dash-text-muted)]">{label}</span>
      </div>
    </div>
  );
}

function ChatbotRow({ data, index, isAdmin, reloadList }) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const chatbotLink = useMemo(() => {
    if (data?.active) return `${data.id}/versions/editor/${data.active}`;
    return `${data.id}/versions/`;
  }, [data]);

  const onToggleActive = useCallback(() => {
    const next = !data.isActive;
    updateChatBot(data.id, { ...data, isActive: next })
      .then(() => {
        toast.success(next ? "Ativado!" : "Desativado!");
        reloadList();
      })
      .catch(() => toast.error("Falha ao atualizar status"));
  }, [data, reloadList]);

  const onDelete = useCallback(() => {
    setDeleting(true);
    deleteChatbot(data.id)
      .then(() => {
        toast.success("Deletado!");
        setConfirmDelete(false);
        reloadList();
      })
      .catch(() => toast.error("Falha ao deletar"))
      .finally(() => setDeleting(false));
  }, [data.id, reloadList]);

  const chartData = useMemo(() => {
    if (!data?.graph?.length) return [];
    const mapped = data.graph.map((d) => ({
      ...d,
      day: Weekdays[new Date(d.timeRange).getDay()],
    }));
    if (mapped.length === 1) return [mapped[0], { ...mapped[0], day: "" }];
    return mapped;
  }, [data.graph]);

  const menuItems = useMemo(
    () => [
      {
        label: data.isActive ? "Desativar" : "Ativar",
        icon: data.isActive ? PowerOff : Power,
        onClick: onToggleActive,
      },
      { separator: true },
      { label: "Deletar", icon: Trash2, danger: true, onClick: () => setConfirmDelete(true) },
    ],
    [data.isActive, onToggleActive]
  );

  return (
    <>
      <div
        className="group flex cursor-pointer items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[var(--dash-surface-hover)]"
        onClick={() => navigate(chatbotLink)}
        style={{ animationDelay: `${index * 30}ms` }}
      >
        <div className="flex w-6 justify-center">
          <div
            className={cn(
              "h-2.5 w-2.5 rounded-full ring-2",
              data.isActive
                ? "bg-[#34fead] ring-[rgba(52,254,173,0.25)]"
                : "bg-[var(--dash-text-faint)] ring-[var(--dash-border)]"
            )}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5 md:flex-row md:items-center md:gap-3">
          <span className="truncate text-sm font-medium text-[var(--dash-text)]">{data.name}</span>
          <div className="flex items-center gap-3 text-[11px] text-[var(--dash-text-muted)] md:hidden">
            <span>{(data.messageInMonth ?? 0).toLocaleString()} msgs</span>
          </div>
        </div>

        {isAdmin && (
          <div className="hidden w-28 md:block">
            <span className="truncate text-[11px] text-[var(--dash-text-muted)]">
              {data.company?.name || "—"}
            </span>
          </div>
        )}

        <div className="hidden w-20 text-right md:block">
          <span className="text-sm font-semibold tabular-nums text-[var(--dash-text)]">
            {(data.messageInMonth ?? 0).toLocaleString()}
          </span>
        </div>

        <div className="hidden w-[72px] md:block">
          {chartData.length > 0 ? (
            <div className="h-7">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
                  <defs>
                    <linearGradient id={`cb-g-${data.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ad34fe" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#ad34fe" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="number"
                    stroke="#ad34fe"
                    strokeWidth={1.2}
                    fill={`url(#cb-g-${data.id})`}
                    dot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <span className="text-[9px] text-[var(--dash-text-faint)]">—</span>
          )}
        </div>

        <div className="w-8" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu
            align="right"
            trigger={
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--dash-text-muted)] opacity-0 transition-all hover:bg-[var(--dash-surface-subtle)] group-hover:opacity-100"
              >
                <MoreHorizontal size={14} />
              </button>
            }
            items={menuItems}
          />
        </div>

        <ChevronRight
          size={13}
          className="shrink-0 text-[var(--dash-text-faint)] opacity-0 transition-opacity group-hover:opacity-100"
        />
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        title="Deletar chatbot?"
        description={`"${data.name}" e todas as versões serão removidos permanentemente.`}
        confirmLabel="Deletar"
        loading={deleting}
      />
    </>
  );
}

const ROW_WIDTHS = [180, 130, 210, 150, 170, 120, 190, 140];

function Bone({ className, style, delay = 0 }) {
  return (
    <div
      className={cn("animate-pulse rounded-lg", className)}
      style={{ background: "var(--dash-surface-hover)", animationDelay: `${delay}ms`, ...style }}
    />
  );
}

function ListSkeleton({ isAdmin }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-2 rounded-xl bg-[var(--dash-surface)] px-3 py-2 ring-1 ring-[var(--dash-border)]"
          >
            <Bone className="h-6 w-6 !rounded-md" delay={i * 80} />
            <div className="flex items-center gap-1.5">
              <Bone className="h-4 w-8" delay={i * 80 + 40} />
              <Bone className="h-3 w-12" delay={i * 80 + 60} />
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
        <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-faint)] md:flex">
          <div className="w-6" />
          <div className="flex-1">Nome</div>
          {isAdmin && <div className="w-28">Empresa</div>}
          <div className="w-20 text-right">Msgs/mês</div>
          <div className="w-[72px]">Tendência</div>
          <div className="w-8" />
        </div>

        <div className="divide-y divide-[var(--dash-border)]">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-3.5" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex w-6 justify-center">
                <Bone className="h-2.5 w-2.5 !rounded-full" delay={i * 60} />
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Bone className="h-4" delay={i * 60 + 30} style={{ width: ROW_WIDTHS[i % ROW_WIDTHS.length] }} />
              </div>
              {isAdmin && (
                <div className="hidden w-28 md:block">
                  <Bone className="h-3 w-20" delay={i * 60 + 40} />
                </div>
              )}
              <div className="hidden w-20 md:flex justify-end">
                <Bone className="h-4 w-10" delay={i * 60 + 60} />
              </div>
              <div className="hidden w-[72px] md:block">
                <Bone className="h-7 w-full !rounded-md" delay={i * 60 + 80} />
              </div>
              <div className="w-8" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SearchFeedback({ loading, hasValue, onClear }) {
  if (loading && hasValue) {
    return <Loader2 size={14} className="animate-spin text-[var(--dash-primary)]" />;
  }
  if (hasValue) {
    return (
      <button
        type="button"
        onClick={onClear}
        className="flex h-5 w-5 items-center justify-center rounded-md text-[var(--dash-text-muted)] transition-colors hover:bg-[var(--dash-surface-hover)] hover:text-[var(--dash-text)]"
      >
        <X size={12} />
      </button>
    );
  }
  return null;
}
